import React, { useRef, useEffect, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import Image from 'next/image';
import Link from 'next/link';

interface ProductEcosystemProps {
  className?: string;
}

const ProductEcosystem: React.FC<ProductEcosystemProps> = ({ className = '' }) => {
  const [isInView, setIsInView] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsInView(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const capabilities = [
    {
      id: 'knowledge',
      title: 'Knowledge becomes a growth asset',
      description: "Sales and support teams learn continuously. What works gets amplified. What doesn't gets fixed automatically, so every interaction improves the next one.",
      icon: 'LightBulbIcon',
      useImage: true,
      imagePath: '/assets/images/Annotation_2026-01-30_162343-1769770433490.png',
      href: 'https://knowmax.ai/',
      color: 'from-accent to-brand-teal',
    },
    {
      id: 'engagement',
      title: 'Engagement becomes intelligent',
      description: 'Human teams and AI agents work together across assisted, digital, video, and physical channels to convert, retain, and grow customers.',
      icon: 'UserGroupIcon',
      useImage: true,
      imagePath: '/assets/images/Annotation_2026-01-30_165210-1769772150724.png',
      href: 'https://phygitalmax.com/',
      color: 'from-primary to-accent',
    },
    {
      id: 'performance',
      title: 'Performance protects profitability',
      description: 'Productivity, security, and compliance are built in, ensuring growth scales without leakage or risk.',
      icon: 'ShieldCheckIcon',
      useImage: true,
      imagePath: '/assets/images/Layer_x0020_1-1770726496170.png',
      href: 'https://leapmax.ai/',
      color: 'from-trust to-success',
    },
    {
      id: 'physical',
      title: 'Physical data points fuel digital decisions',
      description: 'Real-world interactions and environments feed intelligence back into the system, closing the loop between experience and outcome.',
      icon: 'ArrowPathIcon',
      useImage: true,
      imagePath: '/assets/images/Devicemax-logo_1-1770726659660.png',
      href: 'https://devicemax.com/',
      color: 'from-conversion to-warning',
    },
  ];

  return (
    <section id="unifymax-techstack" ref={sectionRef} className={`py-24 lg:py-32 bg-slate-50 ${className}`}>
      <div className="container mx-auto px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">

          {/* Section Header */}
          <div
            className={`text-center mb-16 transition-all duration-1000 ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          >
            <div className="text-xs font-semibold uppercase tracking-widest mb-6 rounded-br-none rounded-t-none rounded-bl-none border-0 text-left">
              <span className="text-xs uppercase tracking-widest mb-6 text-left text-[rgba(27,180,215,1)] border-none font-semibold">PLATFORM</span>
            </div>
            <h2 className="sm:text-5xl font-bold mb-8 max-w-3xl text-5xl text-left" style={{ fontFamily: "'Titillium Web', sans-serif", color: '#000' }}>
              UnifyMax Techstack
            </h2>
            <p className="sm:text-lg mb-12 max-w-2xl text-left text-lg text-[rgba(74,85,104,1)]">
              Four integrated capabilities that transform customer engagement into business growth.
            </p>
          </div>

          {/* Capabilities Grid */}
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {capabilities.map((capability, index) => {
              const CardContent = (
                <div
                  className={`bg-white rounded-2xl p-8 lg:p-10 border border-slate-100 h-full group-hover:border-[#1bb4d7]/30 group-hover:shadow-xl transition-all duration-400 transition-all duration-1000 ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  {capability.useImage ? (
                    <div className="mb-6 h-16 flex items-center">
                      <Image
                        src={capability.imagePath!}
                        alt={capability.title}
                        width={150}
                        height={64}
                        className="object-contain"
                      />
                    </div>
                  ) : (
                    <div className={`bg-gradient-to-br ${capability.color} rounded-xl p-4 inline-block mb-6`}>
                      <Icon name={capability.icon as any} size={28} variant="solid" className="text-white" />
                    </div>
                  )}
                  <h3 className="font-display font-bold text-xl lg:text-2xl text-slate-900 mb-4 leading-snug">
                    {capability.title}
                  </h3>
                  <p className="font-body text-base text-slate-500 leading-relaxed">
                    {capability.description}
                  </p>
                  <div className="mt-6 flex items-center space-x-1 text-[#1bb4d7] text-sm font-semibold opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <span>Learn more</span>
                    <Icon name="ArrowRightIcon" size={14} variant="solid" />
                  </div>
                </div>
              );

              const linkMap: Record<string, string> = {
                engagement: 'https://phygitalmax.com/',
                knowledge: 'https://knowmax.ai/',
                performance: 'https://leapmax.ai/',
                physical: 'https://devicemax.com/',
              };

              if (linkMap[capability.id]) {
                return (
                  <Link
                    key={capability.id}
                    href={linkMap[capability.id]}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block group"
                  >
                    {CardContent}
                  </Link>
                );
              }

              return <div key={capability.id}>{CardContent}</div>;
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductEcosystem;
